// Resolve backend URL defensively:
// 1. Use REACT_APP_BACKEND_URL if set (dev/preview environments).
// 2. Fall back to the current page origin (production — same-origin deploys).
function resolveBackendUrl() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    const isPreview = host.endsWith('.preview.emergentagent.com') || host.endsWith('.emergent.host');
    // In true production (custom domain), always use same-origin to avoid cross-origin cookie/CORS issues.
    if (!isLocal && !isPreview) return window.location.origin;
  }
  return envUrl || (typeof window !== 'undefined' ? window.location.origin : '');
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

// ============================================================================
// SESSION TOKEN STORAGE — belt + suspenders auth
//
// The backend sets an HttpOnly cookie AND returns the session_token in the
// JSON body of /auth/session. On production custom domains (Cloudflare / edge
// proxies) the Set-Cookie header is sometimes stripped or a SameSite=None
// cookie is blocked by browser heuristics. That would cause the "login for
// 2 seconds, then kicked back to login" bug users saw on gurukulveda.com.
//
// To make auth rock-solid we ALSO persist the token in localStorage and send
// it on every API call as `Authorization: Bearer <token>`. The backend's
// get_current_user() already accepts both cookies AND Bearer headers, so
// this is additive — cookies still work where they work.
//
// We ALSO cache the last-known user object in localStorage so the UI can
// hydrate optimistically on page reload without a flash of the login screen
// while /auth/me is in flight. The /auth/me call still runs in the background
// to validate; only a genuine 401 clears the cache.
// ============================================================================
const TOKEN_KEY = 'gv_session_token';
const USER_KEY = 'gv_cached_user';

export function saveSessionToken(token) {
  if (!token || typeof window === 'undefined') return;
  try { window.localStorage.setItem(TOKEN_KEY, token); } catch { /* quota / private mode */ }
}
export function readSessionToken() {
  if (typeof window === 'undefined') return '';
  try { return window.localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
export function clearSessionToken() {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

export function saveCachedUser(user) {
  if (!user || typeof window === 'undefined') return;
  try { window.localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch { /* ignore */ }
}
export function readCachedUser() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
export function clearCachedUser() {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(USER_KEY); } catch { /* ignore */ }
}

function authHeaders() {
  const tok = readSessionToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

/**
 * Authenticated raw fetch — for endpoints that need multipart/FormData
 * bodies (file uploads) which `apiPost` doesn't handle. Sends BOTH the
 * session cookie AND the Bearer token, mirroring `apiPost`'s belt-and-braces
 * auth strategy.
 *
 * Without this, every raw `fetch('/api/upload/file', { credentials: 'include' })`
 * call relies SOLELY on the session cookie — which is unreliable on
 * production custom domains (Cloudflare/edge proxies strip SameSite=None
 * cookies in some browser/version combos). That manifested as the
 * intermittent "upload 401" failures on the Syllabus Bulk Upload page.
 *
 * Usage:
 *   const res = await authedFetch(`${API_BASE}/upload/file`, {
 *     method: 'POST', body: formData,
 *   });
 */
export async function authedFetch(url, opts = {}) {
  const headers = { ...(opts.headers || {}), ...authHeaders() };
  return fetch(url, { credentials: 'include', ...opts, headers });
}

/**
 * Chunked file upload — bypasses CDN edge body-size limits (Cloudflare free
 * tier caps POSTs at 100 MB at the edge, regardless of backend config).
 *
 * Files <= SINGLE_POST_MAX go through the existing single-POST `/upload/file`
 * (faster, simpler). Larger files are sliced in the browser into 5 MB chunks
 * and POSTed individually — each chunk is well under any CDN limit, so the
 * total uploadable file size is unlimited (capped at 2 GB by the backend).
 *
 * Returns the SAME response shape as `/api/upload/file`:
 *   { file_url, filename, stored_name, size, local_cached, mongo_backed }
 * so call sites can use either path without branching on file size.
 *
 * Options:
 *   onProgress(loaded, total) — called after every chunk. Use to drive a UI bar.
 *   signal — AbortSignal; on abort the chunked session is told to clean up.
 *   concurrency — chunks in flight at once (default 3). Higher = faster but
 *     more memory + more parallel connections.
 */
const SINGLE_POST_MAX = 50 * 1024 * 1024;        // 50 MB — fast path for small files
const CHUNK_SIZE = 5 * 1024 * 1024;              // 5 MB per chunk — well under every CDN limit
const CHUNK_RETRIES = 3;                         // per-chunk retry budget
const CHUNK_RETRY_DELAY_MS = 1500;

export async function chunkedUpload(file, { onProgress, signal, concurrency = 3 } = {}) {
  // FAST PATH: small files use the existing single-POST endpoint
  if (file.size <= SINGLE_POST_MAX) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await authedFetch(`${API_BASE}/upload/file`, { method: 'POST', body: fd, signal });
    if (!res.ok) {
      const detail = await readErrorBody(res);
      const e = new Error(detail || `upload ${res.status}`);
      e.status = res.status;
      throw e;
    }
    const out = await res.json();
    if (onProgress) onProgress(file.size, file.size);
    return out;
  }

  // CHUNKED PATH
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // 1) Open a session
  const initRes = await authedFetch(`${API_BASE}/upload/chunk-init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: file.name,
      total_size: file.size,
      total_chunks: totalChunks,
      content_type: file.type || 'application/octet-stream',
    }),
    signal,
  });
  if (!initRes.ok) {
    const detail = await readErrorBody(initRes);
    const e = new Error(detail || `chunk-init ${initRes.status}`);
    e.status = initRes.status;
    throw e;
  }
  const { upload_id: uploadId } = await initRes.json();

  // 2) Upload chunks in parallel with bounded concurrency + per-chunk retry
  let uploadedBytes = 0;
  const queue = Array.from({ length: totalChunks }, (_, i) => i);
  const failures = [];

  async function uploadOneChunk(index) {
    const start = index * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end);
    for (let attempt = 0; attempt < CHUNK_RETRIES; attempt++) {
      if (signal?.aborted) throw new Error('aborted');
      try {
        const fd = new FormData();
        fd.append('chunk', blob);
        const r = await authedFetch(
          `${API_BASE}/upload/chunk/${encodeURIComponent(uploadId)}/${index}`,
          { method: 'POST', body: fd, signal },
        );
        if (r.ok) {
          uploadedBytes += (end - start);
          if (onProgress) onProgress(Math.min(uploadedBytes, file.size), file.size);
          return;
        }
        // 4xx (except 408/429) means a permanent problem with this chunk —
        // don't waste retries on it. 5xx and network errors are retryable.
        if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429) {
          const detail = await readErrorBody(r);
          throw new Error(detail || `chunk ${index} → ${r.status}`);
        }
      } catch (ex) {
        if (signal?.aborted) throw ex;
        if (attempt === CHUNK_RETRIES - 1) throw ex;
      }
      await new Promise(r => setTimeout(r, CHUNK_RETRY_DELAY_MS * (attempt + 1)));
    }
  }

  async function worker() {
    while (queue.length) {
      const idx = queue.shift();
      if (idx === undefined) return;
      try { await uploadOneChunk(idx); }
      catch (ex) { failures.push({ index: idx, error: ex.message }); }
    }
  }

  const workers = Array.from({ length: Math.max(1, concurrency) }, () => worker());
  await Promise.all(workers);

  if (failures.length) {
    // Tell the server to drop the half-finished session so we don't leak
    // pending chunks. Best-effort — don't await failures here.
    try {
      await authedFetch(`${API_BASE}/upload/chunk-abort/${encodeURIComponent(uploadId)}`, { method: 'POST' });
    } catch { /* ignore */ }
    const e = new Error(`${failures.length}/${totalChunks} chunks failed: ${failures[0].error}`);
    e.failures = failures;
    throw e;
  }

  // 3) Finalize — backend stitches chunks + persists to disk + Mongo
  const finRes = await authedFetch(
    `${API_BASE}/upload/chunk-finalize/${encodeURIComponent(uploadId)}`,
    { method: 'POST', signal },
  );
  if (!finRes.ok) {
    const detail = await readErrorBody(finRes);
    const e = new Error(detail || `chunk-finalize ${finRes.status}`);
    e.status = finRes.status;
    throw e;
  }
  return await finRes.json();
}

async function readErrorBody(res) {
  let raw = '';
  try {
    raw = await res.clone().text();
  } catch {
    try { raw = await res.text(); } catch { raw = ''; }
  }
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      if (typeof parsed.detail === 'string') return parsed.detail;
      if (Array.isArray(parsed.detail) && parsed.detail.length) {
        const first = parsed.detail[0];
        if (typeof first === 'string') return first;
        if (first && typeof first.msg === 'string') return first.msg;
      }
      // Structured-object detail (e.g. {reason, message, upgrade_required, ...})
      // — backend uses this for tier-gated endpoints like image illustration.
      if (parsed.detail && typeof parsed.detail === 'object') {
        if (typeof parsed.detail.message === 'string') return parsed.detail.message;
        if (typeof parsed.detail.detail === 'string') return parsed.detail.detail;
      }
      if (typeof parsed.message === 'string') return parsed.message;
    }
  } catch { /* not JSON */ }
  // Gateway/proxy error pages (Cloudflare 502/504 HTML) must never reach a
  // student's screen — show a plain, honest message instead of raw markup.
  const trimmed = raw.trim();
  if (trimmed.startsWith('<') || /<!doctype html/i.test(trimmed)) {
    return res.status >= 500
      ? 'The server is busy right now. Please try again in a moment.'
      : 'Something went wrong. Please try again.';
  }
  return trimmed.slice(0, 300);
}

export async function apiGet(path) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers: { ...authHeaders() },
    });
  } catch (networkErr) {
    // Genuine network failure (offline, DNS, TLS, CORS-blocked) — DO NOT treat
    // as unauthorized. Caller should keep last-known user state and retry.
    const err = new Error('NETWORK_ERROR');
    err.cause = networkErr;
    err.isNetworkError = true;
    throw err;
  }
  if (res.status === 401) {
    const err = new Error('UNAUTHORIZED');
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    const text = await readErrorBody(res);
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
}

export async function apiPost(path, data) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) {
    const text = await readErrorBody(res);
    const err = new Error(text || `Request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function apiPut(path, data) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) {
    const text = await readErrorBody(res);
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
}

export async function apiDelete(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { ...authHeaders() },
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) {
    const text = await readErrorBody(res);
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
}

export async function apiPatch(path, data) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(data),
  });
  if (res.status === 401) throw new Error('UNAUTHORIZED');
  if (!res.ok) {
    const text = await readErrorBody(res);
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Stream a Study Buddy reply over SSE so the answer appears word by word
 * instead of after a 30-second wait. Falls back to the blocking endpoint if
 * streaming is unavailable, so nothing regresses.
 *
 * onDelta(textChunk) fires as the buddy types; resolves with the same payload
 * the non-streaming endpoint returns.
 */
export async function streamChatMessage(sessionId, body, onDelta, signal, onStage) {
  let res;
  try {
    res = await fetch(`${API_BASE}/chat/sessions/${sessionId}/messages/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...authHeaders() },
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    return apiPost(`/chat/sessions/${sessionId}/messages`, body);
  }
  if (res.status === 404 || res.status === 405 || !res.body) {
    return apiPost(`/chat/sessions/${sessionId}/messages`, body);
  }
  if (!res.ok) {
    const text = await readErrorBody(res);
    const err = new Error(text || `Request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let done = null;
  let failure = null;

  while (true) {
    const { value, done: finished } = await reader.read();
    if (finished) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE frames are separated by a blank line; a frame can arrive split.
    let split;
    while ((split = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);
      let event = 'message';
      const dataLines = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (!dataLines.length) continue;
      let payload;
      try { payload = JSON.parse(dataLines.join('\n')); } catch { continue; }
      if (event === 'done') done = payload;
      else if (event === 'error') failure = payload;
      else if (payload.delta) onDelta?.(payload.delta);
      else if (payload.stage) onStage?.(payload.stage);
    }
  }

  if (failure) {
    const err = new Error(failure.detail || 'Something went wrong');
    err.status = failure.status || 500;
    throw err;
  }
  if (!done) throw new Error('The reply got cut off. Try again in a moment.');
  return done;
}
